import axios from 'axios';
import eventFixtureSnapshot from './dev/fixtures/events.snapshot.json';

const secrets = require('./secrets');
const api = secrets.server_url;

// Create configured axios instance
const apiClient = axios.create({
  baseURL: api, // This is your secrets.server_url
  headers: {
    'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
  },
  withCredentials: true
});

const useEventFixture = process.env.REACT_APP_USE_EVENT_FIXTURE === 'true';

function getEventFixtureDebugInfo() {
  return {
    enabled: useEventFixture,
    generatedAt: eventFixtureSnapshot?.generatedAt || null,
    count: typeof eventFixtureSnapshot?.count === 'number'
      ? eventFixtureSnapshot.count
      : getSnapshotRawEvents().length,
    source: eventFixtureSnapshot?.source || null
  };
}

function getSnapshotRawEvents() {
  if (Array.isArray(eventFixtureSnapshot)) {
    return eventFixtureSnapshot;
  }

  if (Array.isArray(eventFixtureSnapshot?.events)) {
    return eventFixtureSnapshot.events;
  }

  return [];
}

function sortProcessedEventsByDate(events) {
  if (!Array.isArray(events) || events.length === 0) {
    return Array.isArray(events) ? events : [];
  }
  events.sort((a, b) => {
    const getRelevantDate = (event) => {
      if (!event.dates || event.dates.length === 0) return 'N/A';
      if (event.frequency === 'Once') {
        return event.dates[0];
      }
      return event.dates[event.dateIndex];
    };

    const dateStrA = getRelevantDate(a);
    const dateStrB = getRelevantDate(b);

    if (dateStrA === 'N/A') return 1;
    if (dateStrB === 'N/A') return -1;

    return new Date(dateStrA) - new Date(dateStrB);
  });

  return events;
}

async function getFixtureHomePageEvents(userLoc) {
  const rawEvents = getSnapshotRawEvents();
  const processedEvents = sortProcessedEventsByDate(processEssentialData(rawEvents));

  const visibleEvents = processedEvents.filter(item =>
    !item.past &&
    item.isApproved &&
    !item.isPrivate
  );

  if (userLoc?.lat && userLoc?.lon) {
    await processLocations(visibleEvents, userLoc.lat, userLoc.lon);
  }

  if (userLoc?.locationAllowed && userLoc?.lat && userLoc?.lon) {
    const nearbyUpcoming = visibleEvents
      .filter(item => item.distanceFromUser === null || item.distanceFromUser <= 100)
      .slice(0, 10);

    // UX fallback for stale snapshots: if no nearby upcoming events,
    // show upcoming events regardless of radius.
    if (nearbyUpcoming.length > 0) {
      return nearbyUpcoming;
    }
  }

  return visibleEvents.slice(0, 10);
}

function getFixtureSingleEvent(eventId) {
  const rawEvents = getSnapshotRawEvents();
  const eventIdStr = String(eventId);
  const isNumericId = !isNaN(eventId) && !isNaN(parseFloat(eventId));

  const matches = rawEvents.filter(event => {
    const candidateIds = [
      event?.id,
      event?._id,
      event?.event_id,
      event?.slug,
      event?.custom_url,
      event?.customUrl,
      event?.event_url
    ].filter(Boolean).map(String);

    return candidateIds.includes(eventIdStr);
  });

  const processedMatches = sortProcessedEventsByDate(processEssentialData(matches));

  if (processedMatches.length === 0) {
    throw new Error('Failed to fetch event');
  }

  if (!isNumericId) {
    return processedMatches.find(item => item.past === false) || processedMatches[0];
  }

  return processedMatches[0];
}

async function getCurrentUser(email) {
  try {
    console.log("getting current user")
    const response = await apiClient.get(`users/${email}`);
    
    // Handle both array and single object responses
    const userData = response?.data?.data;
    if (!userData) return null;
    
    // If it's an array, return the first item, otherwise return the object
    return Array.isArray(userData) ? userData[0] : userData;
  } catch (error) {
    console.error('Failed to fetch current user:', error);
    return null;
  }
}

async function getAllUsers() {
  console.log("getting all users")
  try {
    const usersResponse = await apiClient.get('users/');
    
    return {
      users: usersResponse?.data?.data || []
    };
  } catch (error) {
    console.error('Failed to fetch all users:', error);
    return { users: [] };
  }
}

async function getHomePageEvents(userLoc) {
  console.log("getting home page events")
  console.log('getHomePageEvents')
  if (useEventFixture) {
    return getFixtureHomePageEvents(userLoc);
  }

  try {

    let response

    if (userLoc.locationAllowed) {
      response = await apiClient.get("events/", {
        params: {
          limit: 10,
          upcoming: true,
          lat: userLoc?.lat,
          lon: userLoc?.lon,
          radius: 100
        }
      });
    } else {
      response = await apiClient.get("events/", {
        params: {
          limit: 10,
          upcoming: true,
        }
      });
    }
    // console.log(response)

    const processedEvents = processEssentialData(response.data);

    sortProcessedEventsByDate(processedEvents);

    if (userLoc?.lat && userLoc?.lon) {
      await processLocations(processedEvents, userLoc.lat, userLoc.lon);
    }

    console.log(processedEvents)
    return processedEvents.filter(item =>
      !item.past &&
      item.isApproved &&
      !item.isPrivate
    );
  } catch (error) {
    console.error('Failed to fetch homepage events:', error);
    throw new Error('Failed to fetch homepage events');
  }
}

async function getSingleEvent(eventId) {
  if (useEventFixture) {
    return getFixtureSingleEvent(eventId);
  }

  try {
    console.log(eventId)
    const response = await apiClient.get(`events/${eventId}`);
    console.log(response)
    const processedEvents = processEssentialData([response.data.data]);

    // Custom URL may expand to multiple occurrences; prefer an upcoming one.
    if (!( !isNaN(eventId) && !isNaN(parseFloat(eventId)) )) {
      return (
        processedEvents.find((item) => item.past === false) ??
        processedEvents[0] ??
        null
      );
    }
    return processedEvents[0] ?? null;
  } catch (error) {
    console.error('Failed to fetch event:', error);
    throw new Error('Failed to fetch event');
  }
}

// in information.service.js
async function getBaseData() {
  // Use allSettled so a single missing/failing endpoint (e.g. older BE without
  // /getDefaultOrganization or /getLoggedInUser) doesn't sink the whole load.
  const [userR, emailR, defaultOrgR, locR] = await Promise.allSettled([
    fetchSessionData(),
    fetchSessionEmail(),
    fetchDefaultOrganization(),
    getUserLocation(),
  ]);

  const loggedInUser =
    userR.status === "fulfilled" ? userR.value : { data: null };
  const loggedInEmail =
    emailR.status === "fulfilled" ? emailR.value : { data: null };
  const defaultOrg =
    defaultOrgR.status === "fulfilled" ? defaultOrgR.value : { data: null };
  const userLoc = locR.status === "fulfilled" ? locR.value : null;

  if (userR.status === "rejected")
    console.warn("getBaseData: fetchSessionData failed", userR.reason?.message);
  if (emailR.status === "rejected")
    console.warn(
      "getBaseData: fetchSessionEmail failed",
      emailR.reason?.message
    );
  if (defaultOrgR.status === "rejected")
    console.warn(
      "getBaseData: fetchDefaultOrganization failed",
      defaultOrgR.reason?.message
    );
  if (locR.status === "rejected")
    console.warn("getBaseData: getUserLocation failed", locR.reason?.message);

  return { loggedInUser, loggedInEmail, defaultOrg, userLoc };
}

async function getAllEvents() {
  if (useEventFixture) {
    const rawEvents = getSnapshotRawEvents();
    return sortProcessedEventsByDate(processEssentialData(rawEvents));
  }

  try {
    const response = await apiClient.get("events/");

    const processedEvents = processEssentialData(response.data);

    sortProcessedEventsByDate(processedEvents);

    return processedEvents;
  } catch (error) {
    console.error('Failed to fetch events:', error);
    throw new Error('Failed to fetch all events');
  }
}

const fetchSessionData = async () => {
  const response = await apiClient.get('getLoggedInUser/');
  return response.data;
};

const fetchSessionEmail = async () => {
  const response = await apiClient.get('getLoggedInEmail/');
  return response.data;
};

const fetchDefaultOrganization = async () => {
  const response = await apiClient.get('getDefaultOrganization/');
  return response.data;
};

const getUserDetails = async (loggedInUser, loggedInEmail) => {
  try {
    // If no user is logged in, or email is missing, return null
    if (!loggedInUser?.data || !loggedInEmail?.data) {
      return {
        currentUser: null,
        isAdmin: false
      };
    }
    
    // Get user details by email
    const userResponse = await apiClient.get(`users/${loggedInEmail.data}`);
    const user = userResponse?.data?.data || null;
    
    if (!user) {
      return {
        currentUser: null,
        isAdmin: false
      };
    }
    
    return {
      currentUser: user,
      isAdmin: user.isAdmin || false
    };
  } catch (error) {
    console.error('Failed to fetch user details:', error);
    return {
      currentUser: null,
      isAdmin: false
    };
  }
};

async function getOrganizationInfo(userId, userEmail, defaultOrgId = null) {
  try {
    // If no user is logged in, return empty organization info
    if (!userEmail) {
      return {
        selectedOrganization: defaultOrgId || "",
        selectedOrganizationInfo: null,
        managedOrganizations: []
      };
    }
    
    // Fetch user details to get their organizations
    const userResponse = await apiClient.get(`users/${userEmail}`);
    let user = userResponse?.data?.data;
    
    // Handle array or single object response
    if (Array.isArray(user)) {
      user = user[0];
    }
    
    if (!user) {
      return {
        selectedOrganization: defaultOrgId || "",
        selectedOrganizationInfo: null,
        managedOrganizations: []
      };
    }
    
    console.log("User data from API:", user);
    
    // Use either the session's default org ID or the user's default organization
    const selectedOrganizationName = defaultOrgId || user.defaultOrganization;
    let selectedOrganizationInfo = null;
    
    if (selectedOrganizationName) {
      try {
        // Find organization by name instead of by ID
        const orgsResponse = await apiClient.get('organizations', {
          params: { name: selectedOrganizationName }
        });
        
        // Find the first organization with matching name
        const matchingOrg = orgsResponse?.data?.data?.find(org => 
          org.name === selectedOrganizationName
        );
        
        selectedOrganizationInfo = matchingOrg || null;
      } catch (orgError) {
        console.error('Error fetching default organization:', orgError);
      }
    }
    
    // Get organizations the user is part of
    try {
      // If the user has organizations array, fetch them
      if (user.organizations && user.organizations.length > 0) {
        // Fetch all organizations and filter client-side
        const orgsResponse = await apiClient.get('organizations');
        
        // Filter to only include organizations in the user's list
        const managedOrganizations = orgsResponse?.data?.data?.filter(org => 
          user.organizations.includes(org.name) ||
          org.owner === user.email ||
          (org.co_owner === user.email) ||
          org.admins.some(admin => admin.email === user.email) ||
          org.collaborators.some(collab => collab.email === user.email)
        ) || [];
        
        console.log("User's organizations:", managedOrganizations);
        
        return {
          selectedOrganization: selectedOrganizationName || "",
          selectedOrganizationInfo,
          managedOrganizations
        };
      } else {
        // Fallback to fetching by email
        const orgsResponse = await apiClient.get('organizations');
        
        // Filter to only include organizations where user is a member
        const managedOrganizations = orgsResponse?.data?.data?.filter(org => 
          org.owner === user.email ||
          (org.co_owner === user.email) ||
          org.admins.some(admin => admin.email === user.email) ||
          org.collaborators.some(collab => collab.email === user.email)
        ) || [];
        
        console.log("User's organizations (fallback method):", managedOrganizations);
        
        return {
          selectedOrganization: selectedOrganizationName || "",
          selectedOrganizationInfo,
          managedOrganizations
        };
      }
    } catch (orgsError) {
      console.error('Error fetching user organizations:', orgsError);
      return {
        selectedOrganization: selectedOrganizationName || "",
        selectedOrganizationInfo,
        managedOrganizations: []
      };
    }
  } catch (error) {
    console.error('Failed to fetch organization info:', error);
    return {
      selectedOrganization: defaultOrgId || "",
      selectedOrganizationInfo: null,
      managedOrganizations: []
    };
  }
}

// Helper functions for date and time handling
function parseTimeString(timeString) {
  let endHour = 23;
  let endMinute = 59;
  
  if (timeString) {
    const [hourStr, minuteStr] = timeString.split(':');
    endHour = parseInt(hourStr, 10);
    endMinute = parseInt(minuteStr, 10);
  }
  
  return { hour: endHour, minute: endMinute };
}

// Helper function to check if the current time is before the end time
function isTimeBeforeEndTime(endHour, endMinute) {
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  return currentHour < endHour || (currentHour === endHour && currentMinute < endMinute);
}

// Helper function to normalize dates for comparison (midnight of the given day)
function normalizeDate(date) {
  if (typeof date === 'string') {
    // Handle string dates (like those in the dates array)
    // Use UTC to avoid timezone issues
    const parts = date.split('T')[0].split('-');
    if (parts.length !== 3) {
      return null; // Invalid date format
    }
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
    const day = parseInt(parts[2], 10);
    return Date.UTC(year, month, day);
  } else if (date instanceof Date) {
    // Handle Date objects - convert to UTC midnight
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  } else {
    // Return null for non-date inputs
    return null;
  }
}

function isEventPast(dates, end_time) {
  if (!dates || dates.length === 0) return true;
  
  const now = new Date();
  const today = normalizeDate(now);
  const { hour: endHour, minute: endMinute } = parseTimeString(end_time);
  
  // Check if any event dates are today or in the future
  for (let i = 0; i < dates.length; i++) {
    const eventDate = normalizeDate(dates[i]);
    
    // Skip invalid dates
    if (eventDate === null) {
      continue;
    }
    
    // If the date is in the future, it's definitely not past
    if (eventDate > today) {
      return false;
    }
    
    // If the date is today, check if the event's end time has passed
    if (eventDate === today) {
      // If current time is before the end time, the event is not past
      if (isTimeBeforeEndTime(endHour, endMinute)) {
        return false;
      }
    }
  }
  
  // If we get here, all dates are in the past or today's event has ended
  return true;
}

function getDateIndex(dates, end_time) {
  if (!dates || dates.length === 0) return -1;
  
  let dateIndex = -1;
  const now = new Date();
  const today = normalizeDate(now);
  const { hour: endHour, minute: endMinute } = parseTimeString(end_time);
  
  for (let i = 0; i < dates.length; i++) {
    const eventDate = normalizeDate(dates[i]);
    
    // Skip invalid dates
    if (eventDate === null) {
      continue;
    }
    
    // If the date is today, check if the event's end time has passed
    if (eventDate === today) {
      // If current time is before the end time, use this date
      if (isTimeBeforeEndTime(endHour, endMinute)) {
        dateIndex = i;
        break;
      }
      // Otherwise, the event has ended for today, so continue to the next date
      continue;
    }
    
    // For future dates, just use the first one we find
    if (eventDate > today) {
      dateIndex = i;
      break;
    }
  }
  
  // If no suitable date was found, use the last date
  if (dateIndex === -1 && dates.length > 0) {
    dateIndex = dates.length - 1;
  }
  
  return dateIndex;
}

/** Coerce various API payload shapes into an array of raw event objects. */
function normalizeEventsInput(events) {
  if (Array.isArray(events)) return events;
  if (events && typeof events === 'object') {
    if (Array.isArray(events.data)) return events.data;
    if (Array.isArray(events.events)) return events.events;
    if (Array.isArray(events.results)) return events.results;
  }
  return [];
}

function processEssentialData(events) {
  const list = normalizeEventsInput(events).filter(Boolean);

  return list.map((event) => {
    const processed = {
      ...event,
      tags: event.tags || [],
      dateIndex: -1,
      dates: event.dates || []
    };

    if (event.frequency === "Once") {
      processed.dateIndex = 0
    } else {
      // Use the getDateIndex function for consistency
      processed.dateIndex = getDateIndex(processed.dates, processed.end_time);
    }

    // Convert image URLs
    if (processed.img) {
      processed.img = processed.img.replace(
        "https://ummahfy-event-images.s3.us-east-2.amazonaws.com",
        "https://img.ummahfy.com"
      );
    }

    // Format start date
    const dateOptions = {
      weekday: 'long',
      month: 'short',
      day: 'numeric'
    };

    const timeOptions = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    };

    let startFormatted = "";
    try {
      // Check if we have valid date index
      if (processed.dateIndex >= 0 && processed.dateIndex < processed.dates.length) {
        const startDate = new Date((processed.dates[processed.dateIndex]).split('T')[0] + "T" + (processed.start_time || "00:00:00"));
        const formattedStartDate = new Intl.DateTimeFormat('en-US', dateOptions).format(startDate);
        const formattedStartTime = new Intl.DateTimeFormat('en-US', timeOptions).format(startDate);
        const startTimeWithoutMinutes = formattedStartTime.replace(':00', '').toLowerCase();
        startFormatted = `${formattedStartDate}  •  ${startTimeWithoutMinutes}`;
      } else {
        startFormatted = "Mon, Jan 1 • 12am";
      }
    } catch {
      startFormatted = "Mon, Jan 1 • 12am";
    }

    // Format end date
    let endFormatted = "";
    try {
      if (processed.dates && processed.dates.length > 0) {
        const endDate = new Date((processed.dates[processed.dates.length - 1]).split('T')[0] + "T" + (processed.end_time || "23:59:00"));
        const formattedEndDate = new Intl.DateTimeFormat('en-US', dateOptions).format(endDate);
        const formattedEndTime = new Intl.DateTimeFormat('en-US', timeOptions).format(endDate);
        const endTimeWithoutMinutes = formattedEndTime.replace(':00', '').toLowerCase();
        endFormatted = `${formattedEndDate} | ${endTimeWithoutMinutes}`;
      } else {
        endFormatted = "Monday, January 1st | 12am";
      }
    } catch {
      endFormatted = "Monday, January 1st | 12am";
    }

    // Create timeArray using original format
    processed.timeArray = [startFormatted, endFormatted];

    // Use the isEventPast function for consistency
    processed.past = isEventPast(processed.dates, processed.end_time);

    return processed;
  });
}

async function getUserLocation() {
  // Default location if something goes wrong
  const defaultLocation = {
    city: "Chicago",
    lat: 41.8781,
    lon: 87.6298,
  };

  if (!navigator.geolocation) {
    return defaultLocation;
  }

  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true
      });
    });

    const { latitude, longitude } = position.coords;

    try {
      const cityResponse = await apiClient.get('getCityFromCoords', {
        params: { lat: latitude, lon: longitude }
      });

      return {
        city: cityResponse.data.data,
        lat: latitude,
        lon: longitude,
        locationAllowed: true
      };
    } catch (error) {
      console.error('Error fetching city:', error);
      // Return coordinates but with default city
      return {
        ...defaultLocation,
        lat: latitude,
        lon: longitude,
        locationAllowed: false
      };
    }
  } catch (error) {
    console.error('Location access failed:', error);
    return defaultLocation;
  }
}


function calculateDistance(lat1, lon1, lat2, lon2) {
  const earthRadiusMiles = 3963.0;

  const lat1Rad = lat1 * (Math.PI / 180);
  const lat2Rad = lat2 * (Math.PI / 180);
  const lon1Rad = lon1 * (Math.PI / 180);
  const lon2Rad = lon2 * (Math.PI / 180);

  const distance =
    earthRadiusMiles *
    Math.acos(
      Math.sin(lat1Rad) * Math.sin(lat2Rad) +
      Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.cos(lon2Rad - lon1Rad)
    );

  return distance;
}

async function getEventLoc(placeName) {
  if (!placeName) return null;

  try {
    // Remove the encodeURIComponent here since axios will handle the encoding
    const response = await apiClient.get('getEventLoc/', {
      params: {
        placeName: placeName.trim()  // Just trim, don't encode
      }
    });

    console.log('Location response:', response.data);

    if (response.data.candidates && response.data.candidates.length > 0) {
      const location = response.data.candidates[0].geometry.location;
      return {
        latitude: location.lat,
        longitude: location.lng
      };
    }
    return null;
  } catch (error) {
    console.error('Error getting event location:', error);
    return null;
  }
}

async function processLocations(informationArray, userLatitude, userLongitude) {
  if (!userLatitude || !userLongitude) {
    // If no user location, mark all distances as null
    informationArray.forEach(item => {
      item.distanceFromUser = null;
    });
    return informationArray;
  }

  informationArray.forEach(item => {
    if (!item.latitude || !item.longitude) {
      item.distanceFromUser = null;
      return;
    }

    try {
      item.distanceFromUser = calculateDistance(
        userLatitude,
        userLongitude,
        item.latitude,
        item.longitude
      );
    } catch {
      item.distanceFromUser = null;
    }
  });

  return informationArray;
}

async function getAllOrganizations() {
  try {
    const response = await apiClient.get('organizations/');
    return response?.data?.data || [];
  } catch (error) {
    console.error('Failed to fetch all organizations:', error);
    return [];
  }
}

// if(loggedInEmail){
//   window.heap.identify(loggedInEmail);

// }

export {
  getEventFixtureDebugInfo,
  getCurrentUser,
  getAllUsers,
  getHomePageEvents,
  getSingleEvent,
  getBaseData,
  getAllEvents,
  calculateDistance,
  getOrganizationInfo,
  getAllOrganizations,
  processLocations,
  getEventLoc,
  processEssentialData,
  getUserLocation,
  getUserDetails,
  fetchSessionData,
  fetchSessionEmail,
  getDateIndex,
  isEventPast,
  fetchDefaultOrganization,
};